import React, { useContext, useEffect, useRef } from "react";
import { AuthInteractionStatus } from "../../shared/auth/cognito-browser";
import {
  useCognito,
  useIsAuthenticated,
} from "../../shared/auth/cognito-react";
import { Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PillButton } from "../../shared/components/styled/Button.styled";
import { FlexContainer } from "../../shared/components/styled/Container.styled";
import { LogoNav } from "../../shared/components/styled/Nav.styled";
import Logo from "../../shared/components/Logo";
import {
  LOGIN_BUTTON_LABEL,
  LOGOUT_LOGIN_HELP_MESSAGE,
  LOGOUT_MESSAGE,
} from "../../shared/constants/Login.constants";
import { hex2rgba } from "../../shared/utils/utility-functions";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
  applyBrandingFromSession,
  getStoredBrandingColors,
} from "../../shared/utils/brandingSession";

const Logout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { theme: contextTheme, setTheme } = useContext(ThemeContext);
  const { instance, inProgress } = useCognito();
  const isAuthenticated = useIsAuthenticated();
  const signOutStartedRef = useRef(false);

  const hasMarketingMessage = sessionStorage.getItem("_hm");
  const logoutMessage = sessionStorage.getItem("_m");
  const logoutSubtitle = sessionStorage.getItem("_s");
  const { secondary: storedSecondary, logo: logoutLogo } =
    getStoredBrandingColors();
  const backgroundColor = storedSecondary || theme.palette.secondary.main;

  useEffect(() => {
    applyBrandingFromSession(contextTheme, setTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !isAuthenticated ||
      inProgress !== AuthInteractionStatus.None ||
      signOutStartedRef.current
    ) {
      return;
    }

    signOutStartedRef.current = true;
    instance.logoutRedirect().catch((error) => {
      console.error(error);
      signOutStartedRef.current = false;
    });
  }, [instance, inProgress, isAuthenticated]);

  const triggerAuthGuard = () => {
    navigate("/", { replace: true });
  };

  return (
    <FlexContainer
      padding="25px 0"
      position="relative"
      borderradius="0"
      background={backgroundColor}
      sx={{
        minHeight: "100svh",
        width: "100vw",
      }}
    >
      <LogoNav
        sx={{
          width: "max-content",
          height: "max-content",
          position: "absolute",
          top: {
            xs: "25px",
            md: "25px",
          },
          left: {
            xs: "50%",
            md: "25px",
          },
          bottom: {
            xs: "unset",
            md: "unset",
          },
          transform: {
            xs: "translate(-50%, 0)",
            md: "unset",
          },
        }}
      >
        <LogoNav>
          <Logo height="auto" src={logoutLogo || "/MB-no-padding-white.png"} />
        </LogoNav>
      </LogoNav>
      <FlexContainer
        width="100%"
        height="100%"
        maxWidth="1200px"
        padding="0 25px"
        sx={{
          justifyContent: {
            xs: "center",
            md: "space-between",
          },
          alignItems: "center",
          flexDirection: {
            xs: "column",
            md: "row",
          },
        }}
      >
        <FlexContainer
          flexdirection="column"
          height="100%"
          width="100%"
          padding="25px"
          sx={{
            maxWidth: {
              xs: "400px",
              md: "50%",
            },
            textAlign: {
              xs: "center",
              md: "left",
            },
            alignItems: {
              xs: "center",
              md: "flex-start",
            },
            marginTop: {
              xs: "25px",
            },
          }}
        >
          {hasMarketingMessage === "true" ? (
            <>
              <Typography
                variant="h1"
                component="h1"
                color="secondary.contrastText"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                {logoutMessage}
              </Typography>
              <Typography
                variant="subtitle2"
                color={hex2rgba(theme.palette.secondary.contrastText, 0.6)}
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                  marginTop: { xs: "8px", sm: "12px", md: "16px" },
                }}
              >
                {logoutSubtitle}
              </Typography>
            </>
          ) : null}
        </FlexContainer>
        <FlexContainer
          flexdirection="column"
          padding="50px 25px"
          gap="25px"
          background={theme.palette.background.paper}
          width="clamp(300px, 100%, 400px)"
        >
          <Typography variant="h4">{LOGOUT_MESSAGE}</Typography>
          <Typography>{LOGOUT_LOGIN_HELP_MESSAGE}</Typography>
          <PillButton
            variant="contained"
            color="primary"
            width="100%"
            onClick={() => triggerAuthGuard()}
          >
            {LOGIN_BUTTON_LABEL}
          </PillButton>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

export default Logout;
